import { Check, Heart, Play } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { mediaUrl } from "@/lib/api";

export default function PhotoGrid({
  media,
  slug,
  onOpen,
  selected = [],
  onToggleSelect,
  favorites = [],
  onToggleFavorite,
}) {
  const { t } = useI18n();
  if (!media.length)
    return (
      <p data-testid="gallery-empty" className="py-16 text-center text-sm text-[#5A5752]">
        {t("gallery.empty")}
      </p>
    );

  return (
    <div className="masonry" data-testid="photo-grid">
      {media.map((m, i) => {
        const isSel = selected.includes(m.id);
        const isFav = favorites.includes(m.id);
        const src = m.external_url || mediaUrl(m, "thumb", slug);
        return (
          <figure
            key={m.id}
            data-testid={`media-tile-${m.id}`}
            className={`photo-hover group relative overflow-hidden border bg-white ${
              isSel ? "border-[#8F3329]" : "border-[#C69A3B]/25"
            }`}
          >
            <button
              type="button"
              onClick={() => onOpen(i)}
              aria-label={m.filename}
              className="block w-full"
              data-testid={`media-open-${m.id}`}
            >
              <img src={src} alt={m.caption_es || m.filename} loading="lazy" className="w-full" />
              {m.kind === "video" && (
                <span className="absolute inset-0 grid place-items-center bg-[#252525]/25">
                  <span className="grid h-14 w-14 place-items-center rounded-full border border-[#F6F0E4]/80 bg-[#252525]/50 text-[#F6F0E4]">
                    <Play size={20} />
                  </span>
                </span>
              )}
              {m.watermark && (
                <img
                  src="/hr-creations-logo.png"
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute bottom-2 right-2 h-10 w-10 rounded-full opacity-60 sm:h-12 sm:w-12"
                />
              )}
            </button>
            <div className="absolute right-2 top-2 flex gap-1.5 opacity-100 sm:opacity-0 sm:transition-opacity sm:duration-300 sm:group-hover:opacity-100">
              {onToggleFavorite && (
                <button
                  type="button"
                  data-testid={`favorite-toggle-${m.id}`}
                  aria-label={t("gallery.favorite")}
                  onClick={() => onToggleFavorite(m)}
                  className="grid h-9 w-9 place-items-center border border-[#C69A3B]/60 bg-[#FAFAF7]/90 text-[#8F3329] transition-colors duration-300 hover:bg-[#C69A3B]"
                >
                  <Heart size={15} fill={isFav ? "#8F3329" : "none"} />
                </button>
              )}
              {onToggleSelect && (
                <button
                  type="button"
                  data-testid={`select-toggle-${m.id}`}
                  aria-label={t("gallery.select")}
                  aria-pressed={isSel}
                  onClick={() => onToggleSelect(m)}
                  className={`grid h-9 w-9 place-items-center border transition-colors duration-300 ${
                    isSel
                      ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]"
                      : "border-[#C69A3B]/60 bg-[#FAFAF7]/90 text-[#8F3329] hover:bg-[#C69A3B]"
                  }`}
                >
                  <Check size={15} />
                </button>
              )}
            </div>
            {(m.title_es || m.title_en || m.duration) && (
              <figcaption className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-[#5A5752]">
                <span className="truncate">{m.title_es || m.title_en || m.filename}</span>
                {m.duration ? (
                  <span className="label-mono">
                    {Math.floor(m.duration / 60)}:{String(m.duration % 60).padStart(2, "0")}
                  </span>
                ) : null}
              </figcaption>
            )}
          </figure>
        );
      })}
    </div>
  );
}

export const SelectionBar = ({ count, onDownload, onClear, onFavorite, canDownload }) => {
  const { t } = useI18n();
  if (!count) return null;
  return (
    <div
      data-testid="selection-bar"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-[#C69A3B] bg-[#252525] px-4 py-3 text-[#F6F0E4] sm:px-8"
    >
      <div className="mx-auto flex max-w-[1500px] flex-wrap items-center justify-between gap-3">
        <p className="text-sm" data-testid="selection-count">
          <span className="font-serif-display text-xl text-[#C69A3B]">{count}</span>{" "}
          {count === 1 ? t("gallery.selectedOne") : t("gallery.selected")}
        </p>
        <div className="flex flex-wrap gap-2">
          {canDownload && (
            <button
              type="button"
              data-testid="multi-select-download-btn"
              onClick={onDownload}
              className="min-h-[44px] border border-[#C69A3B] px-5 text-sm transition-colors duration-300 hover:bg-[#C69A3B] hover:text-[#252525]"
            >
              {t("btn.downloadSelected")}
            </button>
          )}
          <button
            type="button"
            data-testid="multi-select-favorite-btn"
            onClick={onFavorite}
            className="min-h-[44px] border border-[#C69A3B]/50 px-5 text-sm transition-colors duration-300 hover:bg-[#C69A3B] hover:text-[#252525]"
          >
            {t("btn.addFavorites")}
          </button>
          <button
            type="button"
            data-testid="multi-select-clear-btn"
            onClick={onClear}
            className="min-h-[44px] px-4 text-sm text-[#E8D8B6] underline transition-colors duration-300 hover:text-[#FAFAF7]"
          >
            {t("btn.clearSelection")}
          </button>
        </div>
      </div>
    </div>
  );
};
